import { useQuery } from '@tanstack/react-query';
import type { TempoPorDimensao } from '@central/contracts';
import { Card, CardBody, CardHeader } from '@/components/Card';
import { formatarMinutos } from '@/lib/format';
import {
  Carregando,
  ComIndicador,
  derivar,
  Falhou,
  Numero,
  Painel,
  ResumoDoRecorte,
  Tabela,
  type ColunaDeTabela,
} from '@/features/relatorios/componentes';
import { useFiltro } from '@/features/relatorios/filtros';
import { GraficoDeTempos } from '@/features/relatorios/graficos';
import { chavesDeRelatorio, obterResolucao } from '@/features/relatorios/relatorios.api';

/**
 * MTRS — tempo médio de restauração, que é a métrica que o ITIL de fato usa.
 *
 * O tempo **útil** e o tempo **corrido** aparecem lado a lado e rotulados como
 * coisas diferentes, porque são. O útil desconta noite, fim de semana e
 * feriado, e sai do relógio de ANS; o corrido é a diferença entre duas datas.
 * Um chamado aberto sexta às 17h e resolvido segunda às 9h tem 16 horas de
 * relógio de parede e talvez 1 hora de expediente — apresentar um pelo outro é
 * como um relatório de ANS mente sem nenhuma linha de código errada.
 */

const COLUNAS: ReadonlyArray<ColunaDeTabela<TempoPorDimensao>> = [
  { cabecalho: 'Item', celula: (l) => l.rotulo, csv: (l) => l.rotulo },
  { cabecalho: 'Amostra', alinharADireita: true, celula: (l) => l.amostra, csv: (l) => l.amostra },
  {
    cabecalho: 'Mediana',
    alinharADireita: true,
    celula: (l) => formatarMinutos(l.medianaMinutos),
    csv: (l) => l.medianaMinutos,
  },
  {
    cabecalho: 'Média',
    alinharADireita: true,
    celula: (l) => formatarMinutos(l.mediaMinutos),
    csv: (l) => l.mediaMinutos,
  },
  {
    cabecalho: '90º percentil',
    alinharADireita: true,
    celula: (l) => formatarMinutos(l.p90Minutos),
    csv: (l) => l.p90Minutos,
  },
];

const paraBarra = (linha: TempoPorDimensao) => ({
  rotulo: linha.rotulo,
  mediana: linha.medianaMinutos,
  p90: linha.p90Minutos,
});

export function ResolucaoPage() {
  const { consulta } = useFiltro();
  const relatorio = useQuery({
    queryKey: chavesDeRelatorio.de('resolucao', consulta),
    queryFn: () => obterResolucao(consulta),
  });

  if (relatorio.isPending) return <Carregando />;
  if (relatorio.isError) {
    return <Falhou erro={relatorio.error} aoTentar={() => void relatorio.refetch()} />;
  }

  const dados = relatorio.data;
  /** Mesma explicação para os três recortes da distribuição. */
  const SEM_AMOSTRA = {
    motivo: 'Não há amostra suficiente para este número.',
    falta: 'Chamados resolvidos no período com relógio de resolução.',
  };

  return (
    <div className="animate-slide-up space-y-6">
      <ResumoDoRecorte recorte={dados.recorte} />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Numero
          rotulo="MTRS — mediana (tempo útil)"
          indicador={derivar(
            dados.tempoUtil,
            (d) => d.medianaMinutos,
            SEM_AMOSTRA,
          )}
          formatar={formatarMinutos}
          ajuda="Como é o dia normal. Desconta expediente, fim de semana e feriado."
        />
        <Numero
          rotulo="MTRS — média (tempo útil)"
          indicador={derivar(dados.tempoUtil, (d) => d.mediaMinutos, SEM_AMOSTRA)}
          formatar={formatarMinutos}
          ajuda="Sensível à cauda: um chamado esquecido puxa este número."
        />
        <Numero
          rotulo="90º percentil (tempo útil)"
          indicador={derivar(dados.tempoUtil, (d) => d.p90Minutos, SEM_AMOSTRA)}
          formatar={formatarMinutos}
          ajuda="Quão ruim fica o dia ruim."
        />
        <Numero
          rotulo="Mediana em tempo corrido"
          indicador={derivar(
            dados.tempoCorrido,
            (d) => d.medianaMinutos,
            SEM_AMOSTRA,
          )}
          formatar={formatarMinutos}
          ajuda="Relógio de parede. Outra métrica — não compare com a de cima."
        />
      </div>

      {/* Cinco colunas de tempo não cabem em meia tela — ver AnsPage. */}
      <div className="grid gap-6 2xl:grid-cols-2">
        <Painel
          titulo="Tempo útil por equipe"
          indicador={dados.utilPorEquipe}
          grafico={(linhas) => <GraficoDeTempos dados={linhas.map(paraBarra)} />}
          colunas={() => COLUNAS}
          chaveDe={(linha) => linha.chave}
          nome={['relatorio', 'resolucao', 'por-equipe']}
          recorte={dados.recorte}
        />
        <Painel
          titulo="Tempo útil por prioridade"
          descricao="Se a curva não cai da baixa para a crítica, a matriz de prioridade não está sendo respeitada."
          indicador={dados.utilPorPrioridade}
          grafico={(linhas) => <GraficoDeTempos dados={linhas.map(paraBarra)} />}
          colunas={() => COLUNAS}
          chaveDe={(linha) => linha.chave}
          nome={['relatorio', 'resolucao', 'por-prioridade']}
          recorte={dados.recorte}
        />
      </div>

      <Card>
        <CardHeader
          title="Primeira resposta"
          description="O tempo até o solicitante saber que alguém pegou o chamado — medido em tempo útil, como o de resolução."
        />
        <CardBody>
          <ComIndicador indicador={dados.primeiraResposta}>
            {(distribuicao, amostra) => (
              <Tabela
                colunas={COLUNAS}
                linhas={[{ chave: 'geral', rotulo: 'Geral', amostra, ...distribuicao }]}
                chaveDe={(linha) => linha.chave}
              />
            )}
          </ComIndicador>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Resolução no primeiro contato (FCR)"
          description="Nomeado pelo ITIL, e a Central ainda não o mede. Está aqui declarado em vez de omitido — e em vez de responder zero."
        />
        <CardBody>
          <ComIndicador indicador={dados.resolucaoNoPrimeiroContato}>
            {(valor) => <p className="text-2xl font-semibold">{valor}</p>}
          </ComIndicador>
        </CardBody>
      </Card>
    </div>
  );
}
